import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { SlaChangeLog } from "@/components/SlaChangeLog";
import { Save, Building2, Timer, Mail, Plus, X, Award } from "lucide-react";

export default function Settings() {
  const [company, setCompany] = useState(null);
  const [stores, setStores] = useState([]);
  const [scope, setScope] = useState("company");
  const [sla, setSla] = useState(null);
  const [labels, setLabels] = useState({});
  const [usesDefault, setUsesDefault] = useState(true);

  useEffect(() => {
    api.get("/company").then((r) => setCompany(r.data.company)).catch(() => {});
    api.get("/stores").then((r) => setStores(r.data.stores)).catch(() => {});
  }, []);

  const loadSla = (store_id) => {
    const q = store_id && store_id !== "company" ? `?store_id=${store_id}` : "";
    api.get(`/sla${q}`).then((r) => { setSla(r.data.stages); setLabels(r.data.labels); setUsesDefault(r.data.uses_company_default_sla); }).catch(() => {});
  };
  useEffect(() => { loadSla(scope); }, [scope]);

  const saveCompany = async () => {
    try { await api.put("/company", company); toast.success("Company updated"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const [newRecipient, setNewRecipient] = useState("");
  const recipients = company?.digest_recipients || [];
  const addRecipient = () => {
    const e = newRecipient.trim().toLowerCase();
    if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error("Enter a valid email"); return; }
    if (recipients.includes(e)) { toast.error("Already added"); return; }
    setCompany({ ...company, digest_recipients: [...recipients, e] });
    setNewRecipient("");
  };
  const removeRecipient = (e) => setCompany({ ...company, digest_recipients: recipients.filter((r) => r !== e) });
  const saveDigest = async () => {
    try {
      await api.put("/company", {
        digest_enabled: !!company.digest_enabled,
        digest_time: company.digest_time || "08:00",
        digest_recipients: recipients,
      });
      toast.success("Digest schedule saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveCertificate = async () => {
    try {
      await api.put("/company", { certificate_enabled: !!company.certificate_enabled });
      toast.success("Certificate setting saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveSla = async () => {
    try {
      const items = Object.entries(sla).map(([stage, v]) => ({ stage, ...v }));
      await api.put("/sla", { store_id: scope === "company" ? null : scope, uses_company_default_sla: scope === "company" ? null : usesDefault, items });
      toast.success("Service targets saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const upd = (stage, field, val) => setSla((s) => ({ ...s, [stage]: { ...s[stage], [field]: Number(val) } }));

  return (
    <DashboardLayout title="Settings" needsStore={false}>
      <Tabs defaultValue="targets">
        <TabsList className="mb-4">
          <TabsTrigger value="targets" data-testid="tab-targets"><Timer size={14} className="mr-1" /> Service Targets</TabsTrigger>
          <TabsTrigger value="company" data-testid="tab-company"><Building2 size={14} className="mr-1" /> Company</TabsTrigger>
          <TabsTrigger value="digest" data-testid="tab-digest"><Mail size={14} className="mr-1" /> Daily Digest</TabsTrigger>
        </TabsList>

        <TabsContent value="targets">
          <div className="bg-card border border-border p-5 max-w-4xl">
            <div className="flex items-center gap-4 mb-4">
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="w-64" data-testid="sla-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Company-wide Defaults</SelectItem>
                  {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.store_name}</SelectItem>)}
                </SelectContent>
              </Select>
              {scope !== "company" && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={usesDefault} onChange={(e) => setUsesDefault(e.target.checked)} data-testid="uses-default-toggle" />
                  Use company defaults for this store
                </label>
              )}
            </div>
            {sla && (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                  <th className="py-2">Stage</th><th className="py-2">Warning (min)</th><th className="py-2">Overdue (min)</th>
                  <th className="py-2">Critical (min)</th><th className="py-2">Reminder (min)</th></tr></thead>
                <tbody>
                  {Object.entries(sla).map(([stage, v]) => (
                    <tr key={stage} className="border-b border-border">
                      <td className="py-2 font-medium">{labels[stage] || stage}</td>
                      {["warning_minutes","overdue_minutes","critical_minutes","reminder_interval_minutes"].map((f) => (
                        <td key={f} className="py-2 pr-3">
                          <Input type="number" className="w-24 h-8" value={v[f]} onChange={(e) => upd(stage, f, e.target.value)} data-testid={`sla-${stage}-${f}`} disabled={scope !== "company" && usesDefault} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <Button className="mt-4 gap-2" onClick={saveSla} data-testid="save-sla-btn"><Save size={15} /> Save Targets</Button>
          </div>
          <div className="max-w-4xl mt-4">
            <SlaChangeLog canUndo onUndone={() => loadSla(scope)} />
          </div>
        </TabsContent>

        <TabsContent value="company">
          {company && (
            <div className="bg-card border border-border p-5 max-w-lg space-y-3">
              {[["company_name","Company Name"],["contact_email","Contact Email"],["contact_number","Contact Number"],["address","Head Office Address"],["logo_url","Logo URL"]].map(([k,l]) => (
                <div key={k}><label className="text-xs uppercase text-muted-foreground">{l}</label>
                  <Input className="mt-1" value={company[k] || ""} onChange={(e) => setCompany({ ...company, [k]: e.target.value })} data-testid={`company-${k}`} /></div>
              ))}
              <div className="text-xs text-muted-foreground">Company code: <span className="font-mono-t">{company.company_code}</span> · Max stores: {company.maximum_active_stores}</div>
              <Button className="gap-2" onClick={saveCompany} data-testid="save-company-btn"><Save size={15} /> Save Company</Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="digest">
          {company && (
            <div className="bg-card border border-border p-5 max-w-lg space-y-4" data-testid="digest-panel">
              <div>
                <div className="font-display text-xl font-bold">Automated Daily Digest</div>
                <p className="text-sm text-muted-foreground mt-1">Email the overdue-accountability digest to your team automatically, once every day.</p>
              </div>
              <label className="flex items-center gap-3 text-sm">
                <input type="checkbox" className="h-4 w-4" checked={!!company.digest_enabled}
                  onChange={(e) => setCompany({ ...company, digest_enabled: e.target.checked })} data-testid="digest-enabled-toggle" />
                Enable automated daily digest
              </label>
              <div>
                <label className="text-xs uppercase text-muted-foreground">Send time (Jamaica time)</label>
                <Input type="time" className="mt-1 w-40" value={company.digest_time || "08:00"}
                  onChange={(e) => setCompany({ ...company, digest_time: e.target.value })} data-testid="digest-time" />
              </div>
              <div>
                <label className="text-xs uppercase text-muted-foreground">Recipients</label>
                <div className="flex gap-2 mt-1">
                  <Input placeholder="name@example.com" value={newRecipient}
                    onChange={(e) => setNewRecipient(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRecipient())} data-testid="digest-recipient-input" />
                  <Button variant="outline" className="gap-1 shrink-0" onClick={addRecipient} data-testid="digest-add-recipient"><Plus size={15} /> Add</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3" data-testid="digest-recipients-list">
                  {recipients.length === 0 && <span className="text-xs text-muted-foreground">No recipients yet — the digest will go to the company contact email.</span>}
                  {recipients.map((r) => (
                    <span key={r} className="flex items-center gap-1 bg-secondary border border-border rounded-full px-3 py-1 text-xs font-mono-t" data-testid={`recipient-${r}`}>
                      {r}
                      <button onClick={() => removeRecipient(r)} className="text-muted-foreground hover:text-alert-red" data-testid={`remove-recipient-${r}`}><X size={13} /></button>
                    </span>
                  ))}
                </div>
              </div>
              <Button className="gap-2" onClick={saveDigest} data-testid="save-digest-btn"><Save size={15} /> Save Digest Schedule</Button>

              <div className="border-t border-border pt-4 mt-2">
                <div className="font-display text-lg font-bold flex items-center gap-2"><Award size={18} className="text-alert-amber" /> Monthly Top-Performer Certificate</div>
                <p className="text-sm text-muted-foreground mt-1 mb-3">Automatically email a Certificate of Excellence to the top performer on the 1st of each month.</p>
                <label className="flex items-center gap-3 text-sm">
                  <input type="checkbox" className="h-4 w-4" checked={!!company.certificate_enabled}
                    onChange={(e) => setCompany({ ...company, certificate_enabled: e.target.checked })} data-testid="cert-enabled-toggle" />
                  Enable automated monthly certificate
                </label>
                <Button className="gap-2 mt-3" onClick={saveCertificate} data-testid="save-cert-btn"><Save size={15} /> Save Certificate Setting</Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
